import React from "react";

import {
  AssetModel,
  type AssetType,
  type CurrencyCode,
  CurrencyCodes,
  type CustomerInvoiceAdjustmentModel,
  type CustomerModel,
  EntityModel,
} from "@doitintl/cmp-models";
import { type WithModel } from "@doitintl/models-admin";
import { getBatch, getCollection, type ModelReference, modelServerTimestamp } from "@doitintl/models-any";
import CloseIcon from "@mui/icons-material/CloseRounded";
import IconButton from "@mui/material/IconButton";
import isEqual from "lodash/isEqual";
import { DateTime } from "luxon";
import Papa from "papaparse";

import { CopyToClipboardButton } from "../../Components/CopyToClipboardButton";
import { useSnackbar } from "../../Components/SharedSnackbar/SharedSnackbar.context";
import { assetTypeName, getCurrencyByCode } from "../../utils/common";
import { assetTypes } from "./AdjustmentFormDialog";

type Row = {
  type: AssetType;
  amount: number;
  start_date: string;
  end_date: string;
  billing_profile: string;
  description: string;
  details: string;
  currency: CurrencyCode;
};

const invoiceAdjustmentsHeaders = [
  "type",
  "billing_profile",
  "description",
  "details",
  "currency",
  "amount",
  "start_date",
  "end_date",
];

const MAX_BATCH_SIZE = 100;

const formatErrorMessage = (errors: { rowNumber: number; message: string }[]): React.ReactNode => (
  <div>
    <div>
      Found {errors.length} {errors.length > 1 ? "errors" : "error"}:
    </div>
    {errors.map((error, index) => (
      <div key={index}>
        Row {error.rowNumber}: {error.message}
      </div>
    ))}
  </div>
);

const validateBasicFields = (row: Row): string[] => {
  const errors: string[] = [];
  const startDate = DateTime.fromFormat(row.start_date, "yyyy-LL-dd", { zone: "utc" });
  const endDate = DateTime.fromFormat(row.end_date, "yyyy-LL-dd", { zone: "utc" });

  const requiredFields = [
    { value: row.type, label: "type" },
    { value: row.billing_profile?.trim(), label: "billing profile" },
    { value: row.description?.trim(), label: "description" },
    { value: row.details?.trim(), label: "details" },
    { value: row.currency, label: "currency" },
    { value: row.amount, label: "amount" },
    { value: row.start_date, label: "start date" },
    { value: row.end_date, label: "end date" },
  ];

  requiredFields.forEach(({ value, label }) => {
    if (!value) errors.push(`Missing ${label}`);
  });

  if (row.type && !assetTypes.includes(row.type)) {
    errors.push(`Invalid type ${row.type}`);
  }
  if (row.currency && !Object.values(CurrencyCodes).includes(row.currency as CurrencyCodes)) {
    errors.push(`Invalid currency code '${row.currency}'. Must be one of: ${Object.values(CurrencyCodes).join(", ")}`);
  }
  if (row.amount && isNaN(row.amount)) {
    errors.push("Invalid amount");
  }
  if (row.start_date && !startDate.isValid) {
    errors.push(`Invalid start date ${row.start_date}: must match pattern YYYY-MM-DD`);
  }
  if (row.end_date && !endDate.isValid) {
    errors.push(`Invalid end date ${row.end_date}: must match pattern YYYY-MM-DD`);
  }
  if (startDate.isValid && endDate.isValid && startDate >= endDate) {
    errors.push("Start date must be before end date");
  }

  return errors;
};

const getCustomerFromEntity = async (entity: { data: WithModel<EntityModel>; ref: ModelReference<EntityModel> }) => {
  const customer = entity.data.customer;
  if (!customer) {
    throw new Error(`No customer found for entity`);
  }

  const customerDocSnap = await customer.get();
  const customerData = customerDocSnap.asModelData();
  if (!customerData) {
    throw new Error(`Customer data not found`);
  }

  return {
    data: customerData as unknown as WithModel<CustomerModel>,
    ref: customerDocSnap.modelRef,
  };
};

const validateEntity = async (billingProfile: string, assetType: AssetType) => {
  const errors: string[] = [];
  let entity: { data: WithModel<EntityModel>; ref: ModelReference<EntityModel> } | null = null;

  try {
    const querySnap = await getCollection(EntityModel).where("priorityId", "==", billingProfile).limit(1).get();

    if (querySnap.empty) {
      errors.push(`Billing profile ${billingProfile} not found`);
      return { errors, entity };
    }

    const entityDocSnap = querySnap.docs[0];
    const entityData = entityDocSnap.asModelData();

    if (!entityData.active) {
      errors.push(`Billing profile ${billingProfile} is inactive`);
      return { errors, entity };
    }

    entity = {
      data: entityData as WithModel<EntityModel>,
      ref: entityDocSnap.modelRef,
    };

    const assetsSnap = await getCollection(AssetModel)
      .where("entity", "==", entity.ref)
      .where("type", "==", assetType)
      .limit(1)
      .get();

    if (assetsSnap.empty) {
      errors.push(`Billing profile ${billingProfile} has no assets of type ${assetTypeName(assetType)}`);
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    errors.push(errorMessage);
  }

  return { errors, entity };
};

const validateCurrency = async (
  row: Row,
  entity: { data: WithModel<EntityModel>; ref: ModelReference<EntityModel> }
): Promise<string | undefined> => {
  const entityCurrency = getCurrencyByCode(entity.data.currency);
  const rowCurrency = getCurrencyByCode(row.currency);
  const allowedCurrencies = [entityCurrency];

  if (entityCurrency !== CurrencyCodes.USD) {
    allowedCurrencies.unshift(CurrencyCodes.USD);
  }

  if (!allowedCurrencies.includes(rowCurrency)) {
    return `Currency ${row.currency} is not valid for billing profile ${row.billing_profile}. Allowed currencies: ${allowedCurrencies.join(", ")}`;
  }
};

const processRowValidation = async (
  rows: Row[],
  showSnackbar: (message: React.ReactNode | string, variant: "error" | "success", errors?: string[]) => void
) => {
  const allErrors: { rowNumber: number; message: string }[] = [];
  const validRows: Array<{ row: Row; entity: { data: WithModel<EntityModel>; ref: ModelReference<EntityModel> } }> = [];

  await Promise.all(
    rows.map(async (row, i) => {
      const rowNumber = i + 1;
      const errors: string[] = [];

      const fieldErrors = validateBasicFields(row);
      errors.push(...fieldErrors);

      if (row.billing_profile && row.type) {
        const { errors: entityErrors, entity } = await validateEntity(row.billing_profile, row.type);
        errors.push(...entityErrors);

        if (entity) {
          const currencyError = await validateCurrency(row, entity);
          if (currencyError) {
            errors.push(currencyError);
          }

          if (!fieldErrors.length && !entityErrors.length) {
            validRows.push({ row, entity });
          }
        }
      }

      errors.forEach((message) => {
        allErrors.push({ rowNumber, message });
      });
    })
  );

  if (allErrors.length > 0) {
    allErrors.sort((a, b) => a.rowNumber - b.rowNumber);
    const formattedErrors = allErrors.map((error) => `Row ${error.rowNumber}: ${error.message}`);
    showSnackbar(formatErrorMessage(allErrors), "error", formattedErrors);
    return [];
  }

  return validRows;
};

const createInvoiceAdjustment = async (
  row: Row,
  entity: { data: WithModel<EntityModel>; ref: ModelReference<EntityModel> },
  userModel: { displayName: string; email: string }
) => {
  const customer = await getCustomerFromEntity(entity);
  const docRef = customer.ref.collection("customerInvoiceAdjustments").newDoc();

  const startDate = DateTime.fromFormat(row.start_date, "yyyy-LL-dd", { zone: "utc" });
  const endDate = DateTime.fromFormat(row.end_date, "yyyy-LL-dd", { zone: "utc" });

  const invoiceMonths: Date[] = [];
  for (let dt = startDate; dt <= endDate; dt = dt.plus({ months: 1 })) {
    invoiceMonths.push(dt.toUTC().startOf("month").toJSDate());
  }

  return {
    docRef: docRef as unknown as ModelReference<CustomerInvoiceAdjustmentModel>,
    data: {
      description: row.description,
      details: row.details,
      amount: row.amount,
      entity: entity.ref,
      customer: customer.ref,
      invoiceMonths,
      timestamp: modelServerTimestamp(),
      updatedBy: { name: userModel.displayName, email: userModel.email },
      currency: row.currency,
      finalized: false,
      type: row.type,
      metadata: {
        customer: {
          primaryDomain: customer.data.primaryDomain,
          name: customer.data.name,
          priorityId: entity.data.priorityId,
        },
      },
    },
  };
};

export const useBatchUpload = (
  setBatchUploadLoading: (loading: boolean) => void,
  userModel: { displayName: string; email: string }
) => {
  const sharedSnackbar = useSnackbar();

  const showSnackbar = (message: React.ReactNode | string, variant: "error" | "success", errors?: string[]) => {
    sharedSnackbar.onOpen({
      message,
      variant,
      autoHideDuration: variant === "error" ? 15000 : 5000,
      action: [
        <IconButton key="close" aria-label="Close" color="inherit" onClick={sharedSnackbar.onClose} size="large">
          {errors && <CopyToClipboardButton key="copy" text={errors.join("\n")} color="inherit" />}
          <CloseIcon />
        </IconButton>,
      ],
    });
  };

  const handleBatchUpload = (filesList: FileList) => {
    const files = Array.from(filesList);
    if (files.length !== 1) return;

    const file = files[0];
    if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
      showSnackbar("Invalid file type.", "error");
      return;
    }

    setBatchUploadLoading(true);

    Papa.parse<Row>(file, {
      header: true,
      dynamicTyping: (field) => field === "amount",
      complete: async (results) => {
        try {
          if (!isEqual(results.meta.fields, invoiceAdjustmentsHeaders)) {
            throw new Error("Invalid CSV file schema.");
          }
          if (!results.data.length) {
            throw new Error("No invoice adjustments to create");
          }
          if (results.data.length > MAX_BATCH_SIZE) {
            throw new Error(`Cannot create more than ${MAX_BATCH_SIZE} invoice adjustments`);
          }

          const validRows = await processRowValidation(results.data, showSnackbar);
          if (!validRows.length) {
            setBatchUploadLoading(false);
            return;
          }

          const batch = getBatch();
          const invoiceAdjustments = await Promise.all(
            validRows.map(({ row, entity }) => createInvoiceAdjustment(row, entity, userModel))
          );

          invoiceAdjustments.forEach(({ docRef, data }) => {
            batch.set(docRef, data);
          });

          await batch.commit();
          showSnackbar(`${validRows.length} invoice adjustments created successfully`, "success");
          setBatchUploadLoading(false);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : "Failed to create invoice adjustments";
          showSnackbar(message, "error");
          setBatchUploadLoading(false);
        }
      },
    });
  };

  return handleBatchUpload;
};
